import React from 'react';
import {
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  Divider,
  Checkbox,
  ListItemText,
  Paper,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FilterList as FilterListIcon, SelectAll as SelectAllIcon } from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { SEARCE_COLORS } from '../../theme';
import { DRI_CHART_HEIGHT } from '../../constants/driInsightsConstants';

export interface ProductDRIDataRow {
  product_name: string;
  average_dri: number;
  employee_count: number;
  bu_count: number;
}

interface AverageDRIByProductBarChartProps {
  availableBUs: string[];
  selectedBUs: string[];
  onSelectedBUsChange: (buses: string[]) => void;
  pendingBUs: string[];
  onPendingBUsChange: (buses: string[]) => void;
  menuAnchor: HTMLElement | null;
  onMenuOpen: (el: HTMLElement) => void;
  onMenuClose: () => void;
  productDriData: ProductDRIDataRow[];
}

/**
 * Average DRI scores by product bar chart with BU filter dropdown.
 */
const AverageDRIByProductBarChart: React.FC<AverageDRIByProductBarChartProps> = ({
  availableBUs,
  selectedBUs,
  onSelectedBUsChange,
  pendingBUs,
  onPendingBUsChange,
  menuAnchor,
  onMenuOpen,
  onMenuClose,
  productDriData,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: `1px solid ${SEARCE_COLORS.neutral_200}`,
        borderRadius: 2,
        backgroundColor: SEARCE_COLORS.white,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary }}>
          Average DRI Scores by Product
        </Typography>
        {availableBUs.length > 0 && (
          <>
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => {
                onPendingBUsChange(selectedBUs);
                onMenuOpen(e.currentTarget);
              }}
              sx={{
                minWidth: 250,
                justifyContent: 'space-between',
                textTransform: 'none',
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedBUs.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                    All BUs
                  </Typography>
                ) : selectedBUs.length === availableBUs.length ? (
                  <Typography variant="body2" color="primary" sx={{ fontSize: '0.75rem' }}>
                    All BUs
                  </Typography>
                ) : (
                  <Typography variant="body2" color="primary" sx={{ fontSize: '0.75rem' }}>
                    {selectedBUs.length} selected
                  </Typography>
                )}
              </Box>
              <FilterListIcon fontSize="small" sx={{ ml: 1 }} />
            </Button>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => {
                onPendingBUsChange(selectedBUs);
                onMenuClose();
              }}
              PaperProps={{
                sx: {
                  maxHeight: 400,
                  minWidth: 300,
                  maxWidth: 500,
                },
              }}
            >
              <MenuItem sx={{ '&:hover': { backgroundColor: 'transparent' }, cursor: 'default', padding: '8px 16px' }} disableRipple>
                <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                  <Button
                    size="small"
                    startIcon={<SelectAllIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPendingBUsChange([...availableBUs]);
                    }}
                    sx={{ fontSize: '0.75rem', minWidth: 'auto', flex: 1, textTransform: 'none' }}
                  >
                    Select All
                  </Button>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPendingBUsChange([]);
                    }}
                    sx={{ fontSize: '0.75rem', minWidth: 'auto', flex: 1, textTransform: 'none' }}
                  >
                    Clear All
                  </Button>
                </Box>
              </MenuItem>
              <Divider />
              {availableBUs.map((bu) => (
                <MenuItem
                  key={bu}
                  onClick={(e) => {
                    e.stopPropagation();
                    const newSelection = pendingBUs.includes(bu)
                      ? pendingBUs.filter((b) => b !== bu)
                      : [...pendingBUs, bu];
                    onPendingBUsChange(newSelection);
                  }}
                >
                  <Checkbox
                    checked={pendingBUs.includes(bu)}
                    sx={{ padding: '4px', '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                  />
                  <ListItemText
                    primary={bu}
                    sx={{
                      '& .MuiTypography-root': {
                        fontSize: '0.875rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                </MenuItem>
              ))}
              <Divider />
              <MenuItem sx={{ '&:hover': { backgroundColor: 'transparent' }, cursor: 'default', padding: '8px 16px' }} disableRipple>
                <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPendingBUsChange(selectedBUs);
                      onMenuClose();
                    }}
                    sx={{ fontSize: '0.75rem', textTransform: 'none', color: SEARCE_COLORS.textSecondary }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectedBUsChange(pendingBUs);
                      onMenuClose();
                    }}
                    sx={{
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      backgroundColor: SEARCE_COLORS.primary,
                      '&:hover': { backgroundColor: SEARCE_COLORS.primary },
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
      <Box sx={{ height: DRI_CHART_HEIGHT + 60, display: 'flex', flexDirection: 'column' }}>
        {productDriData.length > 0 ? (
          <ResponsiveContainer width="100%" height={DRI_CHART_HEIGHT}>
            <BarChart data={productDriData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(SEARCE_COLORS.neutral_300, 0.5)} />
              <XAxis
                dataKey="product_name"
                angle={-45}
                textAnchor="end"
                height={100}
                tick={{ fill: SEARCE_COLORS.textSecondary, fontSize: 12 }}
                interval={0}
              />
              <YAxis
                tick={{ fill: SEARCE_COLORS.textSecondary, fontSize: 12 }}
                label={{
                  value: 'Average DRI Score',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fill: SEARCE_COLORS.textSecondary, fontSize: 12 },
                }}
              />
              <RechartsTooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload as ProductDRIDataRow;
                    return (
                      <Box
                        sx={{
                          backgroundColor: SEARCE_COLORS.white,
                          border: `1px solid ${SEARCE_COLORS.neutral_200}`,
                          borderRadius: 1,
                          p: 1.5,
                          boxShadow: `0 2px 8px ${alpha(SEARCE_COLORS.primary, 0.15)}`,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          {data.product_name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: SEARCE_COLORS.textPrimary }}>
                          <strong>Average DRI:</strong> {data.average_dri.toFixed(2)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary, fontSize: '0.75rem' }}>
                          Employees: {data.employee_count} | BUs: {data.bu_count}
                        </Typography>
                      </Box>
                    );
                  }
                  return null;
                }}
              />
              <Bar dataKey="average_dri" radius={[8, 8, 0, 0]} fill={SEARCE_COLORS.primary} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: DRI_CHART_HEIGHT, gap: 2 }}>
            <Typography variant="h6" sx={{ color: SEARCE_COLORS.textSecondary, fontWeight: 500 }}>
              Select BUs
            </Typography>
            <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
              Please select one or more Business Units from the filter above to view the chart
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default AverageDRIByProductBarChart;
